import {
  DIRECTIONAL_CAMERA_BOTTOM,
  DIRECTIONAL_CAMERA_FAR,
  DIRECTIONAL_CAMERA_LEFT,
  DIRECTIONAL_CAMERA_NEAR,
  DIRECTIONAL_CAMERA_RIGHT,
  DIRECTIONAL_CAMERA_TOP,
  DIRECTIONAL_SHADOW_BIAS,
  LIGHT_PIVOT_OFFSET,
  SHADOW_MAP_SIZE,
} from '@constants'
import { LightProps } from '@types'
import { useAppStore } from '@store'
import { useMemo, useRef } from 'react'
import { DirectionalLight, Object3D, Quaternion, Vector3 } from 'three'
import { LightPivot } from '../../ViewerManager/LightPivot'
import { LightMesh3D } from './LightMesh3D'
import { useFrame } from '@react-three/fiber'
import { resolveSceneLightLocalY } from '@utils/sceneObjects/lights/resolveSceneLightLocalY'

export function DirectionalLight3D({
  id,
  x,
  z,
  textures,
  color,
  intensity,
  matrix,
  catalogID,
}: LightProps) {
  const { castShadow } = textures
  const isLight2 = catalogID === 'Light2'
  const items = useAppStore.use.items()

  // Verificar si hay paredes en la escena
  const hasWalls = Array.from(items.values()).some((item) => item.type === 'walls')

  let finalIntensity = intensity
  if (!hasWalls && catalogID === 'LUZGENERAL') {
    finalIntensity = intensity * 0.95
  } else if (hasWalls && catalogID === 'LUZGENERAL') {
    finalIntensity = intensity * 0.9
  }

  //useMemo para evitar recreaciones
  const directionalLight = useMemo(
    () => new DirectionalLight(color, finalIntensity),
    [color, finalIntensity],
  )
  const targetRef = useRef<Object3D>(null)

  const radius = 20000

  useFrame(() => {
    if (!directionalLight) return

    const pivotPosition = new Vector3()
    const pivotQuaternion = new Quaternion()
    const pivotScale = new Vector3()
    matrix?.decompose(pivotPosition, pivotQuaternion, pivotScale)

    if (isLight2 && targetRef.current) {
      // Validar que los valores no sean NaN antes de calcular
      if (isNaN(x) || isNaN(z) || isNaN(radius) || radius === 0) {
        console.error('Valores inválidos en DirectionalLight3D:', { x, z, radius })
        return
      }

      const acosValue = z / radius
      if (acosValue < -1 || acosValue > 1) {
        console.error('Valor fuera de rango para Math.acos:', { acosValue, z, radius })
        return
      }

      const verticalAngle = Math.acos(acosValue) * (180 / Math.PI) - 90
      const phi = ((verticalAngle + 90) * Math.PI) / 180

      const newY = radius * Math.sin(phi)
      const newZ = radius * Math.cos(phi)

      if (isNaN(newY) || isNaN(newZ)) {
        console.error('Resultados NaN en cálculo de posición de luz:', {
          newY,
          newZ,
          verticalAngle,
          phi,
        })
        return
      }

      directionalLight.position.set(x, newY, newZ)
      directionalLight.target.position.set(0, 1000, 0)
      directionalLight.target.updateMatrixWorld()
      return
    }

    const lightLocalY = resolveSceneLightLocalY(pivotPosition.y, hasWalls)
    directionalLight.position.set(x, lightLocalY, z)
    directionalLight.target.position.set(0, 1000, 0)
    directionalLight.target.updateMatrixWorld()
  })

  return (
    <LightPivot
      pivotKey={`${x}-${z}-${id}-`}
      id={id}
      matrix={matrix}
    >
      {isLight2 ? (
        <primitive
          object={directionalLight}
          castShadow={castShadow.value}
          intensity={finalIntensity}
          color={color}
          shadow-mapSize={8192}
          shadow-bias={-0.0005}
          shadow-camera-near={10}
          shadow-camera-far={40000}
          shadow-camera-left={-20000}
          shadow-camera-right={20000}
          shadow-camera-bottom={-20000}
          shadow-camera-top={20000}
          position={[x, LIGHT_PIVOT_OFFSET, z]}
        />
      ) : (
        <primitive
          object={directionalLight}
          castShadow={castShadow.value}
          intensity={finalIntensity}
          color={color}
          shadow-mapSize={SHADOW_MAP_SIZE}
          shadow-bias={DIRECTIONAL_SHADOW_BIAS}
          shadow-camera-near={DIRECTIONAL_CAMERA_NEAR}
          shadow-camera-far={DIRECTIONAL_CAMERA_FAR}
          shadow-camera-left={DIRECTIONAL_CAMERA_LEFT}
          shadow-camera-right={DIRECTIONAL_CAMERA_RIGHT}
          shadow-camera-bottom={DIRECTIONAL_CAMERA_BOTTOM}
          shadow-camera-top={DIRECTIONAL_CAMERA_TOP}
        />
      )}

      <object3D
        ref={targetRef}
        position={[0, 1000, 0]}
      />

      <LightMesh3D
        id={id}
        catalogID={catalogID}
      />
    </LightPivot>
  )
}
